import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";

export default function FinanceSafetyPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    scannedProjects: 0,
    riskSignals: [],
    marginWarnings: [],
    topRiskProjects: [],
  });

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError("");
        const res = await api.get("/financial-control/risks");
        setData({
          scannedProjects: Number(res?.data?.scannedProjects || 0),
          riskSignals: Array.isArray(res?.data?.riskSignals) ? res.data.riskSignals : [],
          marginWarnings: Array.isArray(res?.data?.marginWarnings) ? res.data.marginWarnings : [],
          topRiskProjects: Array.isArray(res?.data?.topRiskProjects) ? res.data.topRiskProjects : [],
        });
      } catch (err) {
        setError(err?.response?.data?.message || "Kosten & risico analyse laden mislukt");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Financieel & Controle</h2>
            <p className="text-sm text-gray-500">Signalen voor projectverlies, nacalculatie en operationele afwijkingen.</p>
          </div>
          <Link to="/pl/kam/analyse" className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
            Terug naar KAM analyse
          </Link>
        </div>
      </section>

      {error ? <section className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</section> : null}

      <section className="grid gap-3 md:grid-cols-3">
        <Tile label="Gescreende projecten" value={data.scannedProjects} loading={loading} />
        <Tile label="Risico-signalen" value={data.riskSignals.length} loading={loading} tone="warning" />
        <Tile label="Margin waarschuwingen" value={data.marginWarnings.length} loading={loading} tone="danger" />
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <article className="rounded-xl border bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900">Top risico-projecten</h3>
          {loading ? <p className="mt-2 text-sm text-gray-500">Laden...</p> : null}
          {!loading && data.topRiskProjects.length === 0 ? <p className="mt-2 text-sm text-gray-500">Geen risico-projecten gevonden.</p> : null}
          {!loading && data.topRiskProjects.length > 0 ? (
            <ul className="mt-2 space-y-2 text-sm">
              {data.topRiskProjects.map((item, index) => (
                <li key={`${item?.projectId || "x"}-${item?.code || "y"}-${index}`} className="rounded border border-gray-100 p-2">
                  <p className="font-medium text-gray-800">{item?.projectName || "-"}</p>
                  <p className="text-xs text-gray-500">{item?.code || "-"}</p>
                  <p className="text-xs text-gray-600">waarde: {Number(item?.value || 0)} • threshold: {Number(item?.threshold || 0)}</p>
                </li>
              ))}
            </ul>
          ) : null}
        </article>

        <article className="rounded-xl border bg-white p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900">Margin waarschuwingen</h3>
          {loading ? <p className="mt-2 text-sm text-gray-500">Laden...</p> : null}
          {!loading && data.marginWarnings.length === 0 ? <p className="mt-2 text-sm text-gray-500">Geen margin waarschuwingen.</p> : null}
          {!loading && data.marginWarnings.length > 0 ? (
            <ul className="mt-2 space-y-2 text-sm">
              {data.marginWarnings.map((item, index) => (
                <li key={`${item?.projectId || "x"}-${index}`} className="rounded border border-red-100 bg-red-50 p-2">
                  <p className="font-medium text-red-900">{item?.projectName || "-"}</p>
                  <p className="text-xs text-red-800">delta: {Number(item?.delta || 0)} ({Number(item?.deltaPct || 0)}%)</p>
                </li>
              ))}
            </ul>
          ) : null}
        </article>
      </section>
    </div>
  );
}

function Tile({ label, value, loading, tone = "default" }) {
  const cls =
    tone === "danger"
      ? "border-red-200 bg-red-50 text-red-800"
      : tone === "warning"
      ? "border-amber-200 bg-amber-50 text-amber-800"
      : "border-gray-200 bg-white text-gray-900";
  return (
    <article className={`rounded-xl border p-4 shadow-sm ${cls}`}>
      <p className="text-xs uppercase tracking-wide">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{loading ? "..." : value}</p>
    </article>
  );
}
